'use client';

import { ArrowRight } from 'lucide-react';

import { Fragment, useCallback, useMemo, useState } from 'react';

import Link from 'next/link';

import { useRouter } from '@shared/hooks/use-router';

import { LineDashedHorizontalIcon, LineDashedVerticalIcon } from '@/components/icons/svgs';

import {
	BillingCycleToggle,
	PlanCard,
	PricingErrorState,
	PricingSkeleton,
} from '@/features/pricing/components/common';
import { pricingPlans } from '@/features/pricing/constants/plans';
import { PlanConfig } from '@/features/pricing/constants/plans-config';
import { usePricingPlans } from '@/features/pricing/hooks/use-pricing-plans';
import { PlanFeature, PricingPlan } from '@/features/pricing/types';
import { mergePlansWithStaticConfig } from '@/features/pricing/utils/merge-plans';

import { MIXPANEL_APP_FEATURES, mixpanelEvents } from '@/shared/constants/mixpanel-events';
import { APP_ROUTES } from '@/shared/constants/navigation';
import { QUERY_PARAMS } from '@/shared/constants/query-params';
import { emitMixpanelEvent } from '@/shared/utils/mixpanel-utils';

export const PricingSection = () => {
	const router = useRouter();
	const [billingPlan, setBillingPlan] = useState<PricingPlan>(pricingPlans.pro_yearly);
	const { data: pricingData, status, error: pricingError, refetch } = usePricingPlans();

	const isYearlyBilling = billingPlan.id === pricingPlans.pro_yearly.id;

	const redirectToLogin = useCallback(
		(planId: string) => {
			const loginRedirectUrl = `${APP_ROUTES.LOGIN}?${QUERY_PARAMS.REDIRECT_PATH}=${encodeURIComponent(`${APP_ROUTES.PLANS}?${QUERY_PARAMS.CHECKOUT}=${planId}`)}`;
			router.push(loginRedirectUrl);
		},
		[router]
	);

	const handlePlanButtonClick = useCallback(
		(plan: PlanConfig) => {
			emitMixpanelEvent(mixpanelEvents.CLICKED_LANDING_PAGE_PRICING_START_BUTTON, {
				feature: MIXPANEL_APP_FEATURES.LANDING_PAGE,
			});
			const planId = isYearlyBilling ? plan.planIds.yearly : plan.planIds.monthly;
			redirectToLogin(planId);
		},
		[isYearlyBilling, redirectToLogin]
	);

	const handleSeeMoreDetailsClick = useCallback(() => {
		emitMixpanelEvent(mixpanelEvents.CLICKED_LANDING_PAGE_PRICING, {
			feature: MIXPANEL_APP_FEATURES.LANDING_PAGE,
		});
	}, []);

	const planCards = useMemo(() => {
		if (!pricingData) return [];

		const isCreditFeature = (feature: PlanFeature) => {
			const text = typeof feature === 'string' ? feature : feature.text;
			return /credit/i.test(text);
		};

		return mergePlansWithStaticConfig(pricingData).map((plan) => ({
			plan: {
				...plan,
				features: plan.features
					.filter((feature) => !isCreditFeature(feature))
					.map((feature) => {
						if (typeof feature === 'string') return feature;
						const { href: _href, ...rest } = feature;
						return rest;
					}),
			},
			price: isYearlyBilling ? plan.prices.yearly : plan.prices.monthly,
		}));
	}, [pricingData, isYearlyBilling]);

	return (
		<section className="landing-width mb-24 flex w-full flex-col items-center md:mb-32">
			<h2 className="landing-heading text-center text-balance !text-[32px] tracking-tight md:!text-[48px]">
				No nonsense pricing.
			</h2>
			<p className="mb-10 mt-5 max-w-3xl text-center text-pretty text-base leading-relaxed text-[hsl(215_20%_38%)] md:mb-14 md:mt-6 md:text-[1.375rem] md:leading-8">
				No per-user pricing. Bring unlimited team members, create unlimited jobs and manage
				unlimited candidates in all plans.
			</p>

			<BillingCycleToggle
				plan={billingPlan}
				onPlanChange={setBillingPlan}
				className="justify-center"
				compact
			/>

			{status === 'pending' && <PricingSkeleton />}
			{status === 'error' && (
				<PricingErrorState
					onRetry={refetch}
					source="landing_pricing"
					error={pricingError}
				/>
			)}
			{status === 'success' && (
				<div className="flex w-full flex-col overflow-hidden rounded-lg border border-slate-200 bg-white shadow-sm md:flex-row md:overflow-visible">
					{planCards.map(({ plan, price }, index) => {
						const isLastCard = index === planCards.length - 1;

						return (
							<Fragment key={plan.name}>
								<PlanCard
									plan={plan.name}
									features={plan.features}
									price={price}
									priceSubtext={plan.priceSubtext}
									buttonText={plan.buttonText}
									onButtonClick={() => handlePlanButtonClick(plan)}
									buttonDisabled={false}
									primaryStyledButton={plan.name === 'Pro'}
									compact
								/>

								{!isLastCard && (
									<div className="mx-auto my-2 w-[88%] overflow-hidden md:hidden">
										<LineDashedHorizontalIcon />
									</div>
								)}
								{!isLastCard && (
									<div className="hidden items-center justify-center md:flex">
										<LineDashedVerticalIcon />
									</div>
								)}
							</Fragment>
						);
					})}
				</div>
			)}

			{status === 'success' && (
				<Link
					href={APP_ROUTES.PRICING}
					className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-border bg-white px-4 py-2 text-sm font-medium text-primary/80 no-underline transition-colors hover:bg-accent md:mt-8"
					onClick={handleSeeMoreDetailsClick}
				>
					See more details
					<ArrowRight className="size-4 shrink-0" />
				</Link>
			)}
		</section>
	);
};
